import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './GiftCardAmount.module.css';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from '../../../context/GlobalContext';

const FieldError = ({ error, id }) => {
  if (!error) return null;
  return (
    <div className="field-error" id={`error-${id}`} role="alert">
      <span className="error-icon" aria-hidden="true">⚠️</span>
      <span className="error-message">{error}</span>
    </div>
  );
};

const GiftCardAmount = ({
  offers = [],
  extrasOffers = [],
  selectedStore,
  onSelectAmount,
  selectedAmount,
  lang = 'fr',
  store
}) => {
  const { t } = useTranslation();
  const [customActive, setCustomActive] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isCustomValid, setIsCustomValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const { currentLanguage, formatAmounts } = useGlobalContext();

  const isAmexSelected = store.useAmex || false;
  const amountOptions = [];
  const customInputRef = useRef(null);
  const isInitialMount = useRef(true);
  const isAvailableAmount = (amount) => {

    let available = false;
    offers.forEach((offer, offerIndex) => {
      if (offer) {
        if (
          offer.type === 'single' &&
          offer.value === amount &&
          offer.availability &&
          offer.availability[selectedStore] > 0
        ) {
          available = true;
        } else if (offer.type === 'slider' && offer.values) {
          offer.values.forEach((val, valIndex) => {
            if (
              val.value === amount &&
              val.availability &&
              val.availability[selectedStore] > 0
            ) {
              available = true;
            }
          });
        }
      }
    });

    return available;
  };

  const isAvailableExtraAmount = (amount) => {
    return extrasOffers.some(offer =>
      offer &&
      offer.value === amount &&
      offer.availability &&
      offer.availability[selectedStore] > 0
    );
  };

  offers.forEach((offer, index) => {
    if (index === 3 && !isAmexSelected) {
      return;
    }

    if (offer && offer.type === 'single' && offer.value != null && isAvailableAmount(offer.value)) {
      amountOptions.push({
        value: offer.value,
        label: formatAmounts(offer.value),
        isAmex: index === 3,
      });
    } else if (offer && offer.type === 'slider' && offer.values && offer.values.length > 0) {
      offer.values.forEach((val) => {
        if (isAvailableAmount(val.value)) {
          amountOptions.push({
            value: val.value,
            label: formatAmounts(val.value),
            isAmex: index === 3
          });
        }
      });
    }
  });

  extrasOffers.forEach((extraOffer) => {
    if (extraOffer &&
      extraOffer.type === 'single' &&
      extraOffer.value != null &&
      isAvailableExtraAmount(extraOffer.value)) {

      const alreadyExists = amountOptions.some(option => option.value === extraOffer.value);

      if (!alreadyExists) {
        amountOptions.push({
          value: extraOffer.value,
          label: formatAmounts(extraOffer.value),
          isAmex: false,
          isExtra: true
        });
      }
    }
  });

  amountOptions.sort((a, b) => a.value - b.value);

  const manualOffer = offers.find((offer) => offer && offer.type === 'manual');

  useEffect(() => {
    if (isInitialMount.current) {
      if ((selectedAmount === null || selectedAmount === undefined || selectedAmount === '') &&
        amountOptions.length === 1 && !manualOffer) {
        onSelectAmount(amountOptions[0].value);
      }
      isInitialMount.current = false;
    }
  }, [selectedAmount, amountOptions, onSelectAmount, manualOffer]);

  const handleCustomButtonClick = () => {
    setCustomActive(true);
    onSelectAmount(null);
    setErrorMessage('');
  };

  const handleCustomValueChange = (e) => {
    const value = e.target.value;
    setCustomValue(value);
    setIsCustomValid(null);
    setErrorMessage('');
  };

  const validateCustomAmount = () => {
    if (!manualOffer) return;

    const numericValue = Number(customValue);
    let isValid = false;

    if (isNaN(numericValue) || customValue.trim() === '') {
      setErrorMessage(t('Veuillez entrer un montant valide'));
    } else {
      const isPredefinedAmount = amountOptions.some(option => option.value === numericValue);
      const isInManualRange = numericValue >= manualOffer.min && (manualOffer.max ? numericValue <= manualOffer.max : true);

      if (isPredefinedAmount || isInManualRange) {
        isValid = true;
        setErrorMessage('');
      } else {
        setErrorMessage(t('Le montant saisi n\'est pas disponible.'));
      }
    }

    setIsCustomValid(isValid);
    setHasBlurred(true);

    if (isValid) {
      onSelectAmount(numericValue);
    } else {
      onSelectAmount(null);
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      validateCustomAmount();
    }
  };

  useEffect(() => {
    if (customActive && customInputRef.current) {
      setTimeout(() => {
        customInputRef.current.focus();
      }, 0);
    }
  }, [customActive]);

  return (
    <div className={styles.container}>
      <h2 className="h2 rfTitle" style={{ padding: 0 }}>{t("Choisissez un montant.")}</h2>
      <div className="montant manual" role="radiogroup" aria-label={t("Choisissez un montant.")}>
        {amountOptions.map((option) => (
          <label key={option.value} className="montantItem">
            <input
              type="radio"
              name="montantChoice"
              value={option.value}
              checked={!customActive && selectedAmount === option.value}
              onChange={() => {
                setCustomActive(false);
                setCustomValue('');
                setErrorMessage('');
                onSelectAmount(option.value);
              }}
            />
            <span className="rfPill">
              {option.label}
              {option.isAmex && <span className={styles.amexIndicator}></span>}
            </span>
          </label>
        ))}
        {manualOffer && (customActive ? (
          <div className="montantItem">
            <span className="rfPill custommontant">
              <input
                ref={customInputRef}
                type="number"
                className={`${styles.customAmount} ${hasBlurred && isCustomValid ? styles.valid :
                  hasBlurred && isCustomValid === false ? styles.invalid : ''
                  }`}
                aria-label={t("Autre montant")}
                value={customValue}
                onChange={handleCustomValueChange}
                onBlur={validateCustomAmount}
                onKeyPress={handleKeyPress}
                min={manualOffer.min}
                max={manualOffer.max}
                aria-describedby={errorMessage ? "error-custom-amount" : undefined}
              />
            </span>
          </div>
        ) : (
          <label className="montantItem">
            <input
              type="radio"
              name="montantChoice"
              value="custom"
              checked={false}
              onChange={handleCustomButtonClick}
            />
            <span className="rfPill">{t("Autre montant")}</span>
          </label>
        ))}
        {manualOffer && customActive && (
          <FieldError error={errorMessage} id="custom-amount" />
        )}
      </div>
      {manualOffer && customActive && (
        <p className="amountRangeNote">
          {t("Saisissez un montant entre {{min}} et {{max}}", {
            min: formatAmounts(manualOffer.min),
            max: formatAmounts(manualOffer.max),
          })}
        </p>
      )}
    </div>
  );
};

GiftCardAmount.propTypes = {
  offers: PropTypes.arrayOf(PropTypes.object),
  extrasOffers: PropTypes.arrayOf(PropTypes.object),
  selectedStore: PropTypes.string.isRequired,
  onSelectAmount: PropTypes.func.isRequired,
  selectedAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lang: PropTypes.string,
  store: PropTypes.object.isRequired,
};

export default GiftCardAmount;